// /write-review?product=<handle> — public review form. The product field
// is locked to the PDP the visitor came from. Submissions land as pending
// for ops to verify the order number in /admin/reviews.
import { useEffect, useState } from 'react';
import { useSeoMeta } from '../lib/seo';
import { Link, useSearchParams } from 'react-router-dom';
import { Star } from 'lucide-react';
import TopUtilityBar from '../sections/TopUtilityBar';
import MainHeader from '../sections/MainHeader';
import Footer from '../sections/Footer';
import { API_BASE } from '../lib/apiBase';
import { productByHandle } from '../lib/shopify/queries';
import ReviewPhotos from '../components/pdp/ReviewPhotos';

const ERROR_COPY = {
        product_required: 'Open this form from a product page.',
        rating_1_to_5: 'Pick a star rating.',
        name_required: 'Add your name.',
        valid_email_required: 'Enter a valid email.',
        order_number_required: 'Add your order number.',
        review_min_10_chars: 'Write at least 10 characters.',
        bad_image: 'One of the photos did not upload. Remove it and try again.',
        unknown_product: 'That product could not be found.',
};

const inputCls = 'w-full bg-transparent border border-white/20 px-3 py-3 font-sans text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF9F1C] transition-colors';
const labelCls = 'block font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2';

function StarPicker({ value, onChange }) {
        const [hover, setHover] = useState(0);
        return (
                <div className="flex gap-1" onMouseLeave={() => setHover(0)} data-testid="review-star-picker">
                        {[1, 2, 3, 4, 5].map((i) => (
                                <button key={i} type="button" onClick={() => onChange(i)} onMouseEnter={() => setHover(i)} aria-label={`${i} star${i > 1 ? 's' : ''}`} data-testid={`review-star-${i}`} className="p-0.5 transition-transform hover:scale-110">
                                        <Star className={`h-8 w-8 ${i <= (hover || value) ? 'fill-[#FF9F1C] text-[#FF9F1C]' : 'text-white/25'}`} />
                                </button>
                        ))}
                </div>
        );
}

function Field({ label, children }) {
        return <label className="block"><span className={labelCls}>{label}</span>{children}</label>;
}

export default function WriteReviewPage() {
        const [params] = useSearchParams();
        const handle = params.get('product') || '';
        const [product, setProduct] = useState(null);
        const [form, setForm] = useState({ rating: 0, author_name: '', author_email: '', order_number: params.get('order') ? `#${params.get('order').replace(/^#/, '')}` : '', title: '', body: '' });
        const [photos, setPhotos] = useState([]);
        const [busy, setBusy] = useState(false);
        const [errors, setErrors] = useState([]);
        const [done, setDone] = useState(false);
        const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

        useSeoMeta({ title: 'Write a review — Loadout', path: '/write-review', noindex: true });
        useEffect(() => {
                if (handle) productByHandle(handle).then(setProduct).catch(() => setProduct(null));
        }, [handle]);

        const submit = async (e) => {
                e.preventDefault();
                setBusy(true);
                setErrors([]);
                try {
                        const r = await fetch(`${API_BASE}/api/reviews`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ ...form, product_handle: handle, image_ids: photos.map((p) => p.id) }),
                        });
                        const j = await r.json().catch(() => ({}));
                        if (r.ok && j.ok) setDone(true);
                        else setErrors(String(j.detail || 'submit_failed').split(','));
                } catch {
                        setErrors(['network']);
                } finally {
                        setBusy(false);
                }
        };

        return (
                <div className="min-h-screen bg-[#0A0A0A] text-white">
                        <TopUtilityBar />
                        <MainHeader />
                        <main data-testid="write-review-page" className="mx-auto max-w-[720px] px-6 py-12 md:py-16">
                                <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#FF9F1C]">Loadout / Reviews</p>
                                <h1 className="mt-3 font-display text-4xl sm:text-5xl uppercase leading-none">Write a review</h1>
                                {done ? (
                                        <div data-testid="write-review-success" className="mt-10 border border-[#FF9F1C]/50 p-6">
                                                <p className="font-display text-[22px] uppercase">Review received.</p>
                                                <p className="mt-2 font-sans text-[14px] text-white/70">Thanks, operator. We verify every review against its order before it goes live.</p>
                                                {handle && <Link to={`/p/${handle}`} className="mt-4 inline-block font-mono text-[11px] tracking-[0.18em] uppercase text-[#FF9F1C] hover:underline">← Back to product</Link>}
                                        </div>
                                ) : (
                                        <form onSubmit={submit} className="mt-10 space-y-6">
                                                <Field label="Product">
                                                        <input data-testid="write-review-product" value={product?.title || handle || 'Open this form from a product page'} disabled readOnly className={`${inputCls} opacity-50 cursor-not-allowed bg-white/[0.04]`} />
                                                </Field>
                                                <div>
                                                        <span className={labelCls}>Your rating</span>
                                                        <StarPicker value={form.rating} onChange={(v) => setForm((f) => ({ ...f, rating: v }))} />
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                                        <Field label="Name"><input data-testid="write-review-name" value={form.author_name} onChange={set('author_name')} maxLength={80} className={inputCls} /></Field>
                                                        <Field label="Email"><input data-testid="write-review-email" type="email" value={form.author_email} onChange={set('author_email')} maxLength={160} className={inputCls} /></Field>
                                                </div>
                                                <Field label="Order number"><input data-testid="write-review-order" value={form.order_number} onChange={set('order_number')} placeholder="#23063" maxLength={40} className={inputCls} /></Field>
                                                <Field label="Review title (optional)"><input data-testid="write-review-title" value={form.title} onChange={set('title')} maxLength={120} className={inputCls} /></Field>
                                                <Field label="Your review"><textarea data-testid="write-review-body" value={form.body} onChange={set('body')} rows={6} maxLength={3000} className={inputCls} /></Field>
                                                <ReviewPhotos photos={photos} setPhotos={setPhotos} />
                                                {errors.length > 0 && (
                                                        <ul data-testid="write-review-errors" className="font-mono text-[11px] text-red-400 space-y-1">
                                                                {errors.map((k) => <li key={k}>{ERROR_COPY[k] || 'Could not submit. Try again.'}</li>)}
                                                        </ul>
                                                )}
                                                <button type="submit" disabled={busy || !handle} data-testid="write-review-submit" className="w-full border border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] font-mono text-[12px] tracking-[0.2em] uppercase py-4 hover:bg-transparent hover:text-[#FF9F1C] transition-colors disabled:opacity-40">
                                                        {busy ? 'Submitting…' : 'Submit review'}
                                                </button>
                                        </form>
                                )}
                        </main>
                        <Footer />
                </div>
        );
}
